import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import {
  Collapse,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  withStyles,
} from "@material-ui/core";
import MuiListItem from "@material-ui/core/ListItem";
import DeleteIcon from "@material-ui/icons/Delete";

import {
  is_side_menu_basic_details_disabled,
  is_user_can_create_approval_request,
} from "../../CompleteSetup/CompleteSetupUtils";
import {
  is_user_admin,
  is_user_maf_admin,
} from "../../../utils/ApplicationUtils";

import {
  ALERT_TYPES,
  APP_ROUTES,
  SERVICE_TYPES,
} from "../../../config/constants";
import CheckedIcon from "../../assets/svg/CheckedIcon.svg";
import DashboardActive from "../../assets/svg/DashboardActive.svg";
import DashboardMenu from "../../assets/svg/DashboardMenu.svg";
import IconArrowClose from "../../assets/svg/IconArrowClose.svg";
import ProductsMenu from "../../assets/svg/ProductsMenu.svg";
import ServiceActive from "../../assets/svg/Shape.svg";
import sideBarStyles from "./SideBarStyles";
import { isArrayValid, isEmptyOrNull } from "../../../utils/DataUtils";
import DeleteServiceTypeDialog from "./Utils/DeleteServiceTypeDialog";
import { getPosAddress, posDetailsInRequiredFormat } from "../../BasicDetails/Helper";
import { updatePOS } from "../redux/posSlice";
import CustomAlert from "components/common/CustomAlert/CustomAlert";
import { STORE_SERVICE_LABELS, STORE_SERVICE_SUCCESS_MESSAGES } from "components/common/App/app-constant";

const ListItem = withStyles({
  root: {
    "&$selected": {
      backgroundColor: "#E9EFF2",
      color: "#0E5AA7",
      fill: "#0E5AA7",
    },
    "&$selected:hover": {
      backgroundColor: "#E9EFF2",
      color: "#0E5AA7",
      fill: "#0E5AA7",
    },
    "&:hover": {
      backgroundColor: "#E9EFF2",
      color: "#0E5AA7",
      fill: "#0E5AA7",
    },
  },
  selected: {},
})(MuiListItem);

function SideBar({ userInfo }) {
  const sideMenuClasses = sideBarStyles();
  const { pathname } = useLocation();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { posDetails, isCreated, isUpdated } = useSelector(
    (state) => state.pos
  );
  const {
    currentCountry: { name: currentCountryName },
  } = useSelector((state) => state.appConfig);

  let enableServiceType = posDetails?.services?.filter((item) =>
    userInfo?.userProfile?.serviceTypeId?.includes(item?.id)
  );

  const [sideBarState, setSideBarState] = useState({
    openServices: true,
    openDeleteServiceTypeDialog: false,
    serviceId: null,
    serviceDisplayName: "",
  });

  const handleServiceClick = () => {
    setSideBarState({
      ...sideBarState,
      openServices: !sideBarState.openServices,
    });
  };

  const handleDeleteService = (event, serviceId, serviceDisplayName) => {
    event.stopPropagation();
    setSideBarState({
      ...sideBarState,
      openDeleteServiceTypeDialog: !sideBarState.openDeleteServiceTypeDialog,
      serviceId,
      serviceDisplayName,
    });
  };

  const handleDeleteServiceType = async () => {
    const serviceTypeIds =
      posDetails?.serviceType?.map((service) => service?.serviceTypeId) || [];
    let serviceType = serviceTypeIds.filter(
      (id) => id !== sideBarState.serviceId
    );

    if (isArrayValid(serviceType)) {
      serviceType = serviceType.join(", ");
    } else {
      serviceType = "";
    }

    const posAddress = getPosAddress(posDetails, currentCountryName);
    const requestPayload = posDetailsInRequiredFormat(
      { ...posDetails, posNumber: posDetails?.posNo ?? "" },
      serviceType,
      posAddress,
      currentCountryName
    );
    const { payload } = await dispatch(updatePOS(requestPayload)); //NOSONAR
    setSideBarState({
      ...sideBarState,
      openDeleteServiceTypeDialog: !sideBarState.openDeleteServiceTypeDialog,
      serviceId: null,
      serviceDisplayName: "",
    });
    if (!isEmptyOrNull(payload?.id)) {
      CustomAlert(
        ALERT_TYPES.SUCCESS,
        STORE_SERVICE_SUCCESS_MESSAGES.POS_UPDATED
      );
      const serviceName = payload?.services?.[0]?.name?.toLowerCase();
      let url = `${APP_ROUTES.COMPLETE_SETUP}/${posDetails?.posNo ?? ""}`;
      if (!isEmptyOrNull(serviceName)) {
        url = `${APP_ROUTES.CREATE_POS}${APP_ROUTES.SERVICE_AND_SLOTS}/${serviceName}/${posDetails?.posNo ?? ""}`;
      }
      navigate(url);
    }

    if (payload?.message) {
      CustomAlert(ALERT_TYPES.ERROR, payload.message);
    }
  };
  
  const renderServiceListItem = (service) => {
    if (isEmptyOrNull(service?.name)) {
      return null;
    }

    const serviceNameUpper = service.name.toUpperCase();
    const serviceNameLower = service.name.toLowerCase();

    if (serviceNameUpper === SERVICE_TYPES.FBC) {
      return (
        <ListItem
          key={service.id}
          className={sideMenuClasses.nested}
          disabled={true}
        >
          <ListItemText
            primary={service.displayName}
            classes={{ primary: sideMenuClasses.listItemSpan }}
          />
        </ListItem>
      );
    }

    let isDisabled = false;
    if (is_user_admin(userInfo)) {
      if (
        serviceNameUpper === SERVICE_TYPES.STANDARD ||
        serviceNameUpper === SERVICE_TYPES.EXPRESS
      ) {
        isDisabled = !is_user_can_create_approval_request(userInfo);
      } else {
        isDisabled = true;
      }
    }

    return (
      <ListItem
        key={service.id}
        button
        component={NavLink}
        to={`${APP_ROUTES.CREATE_POS}${APP_ROUTES.SERVICE_AND_SLOTS}/${serviceNameLower}/${posDetails?.posNo}`}
        className={sideMenuClasses.nested}
        selected={pathname.includes(serviceNameLower)}
        disabled={isDisabled}
      >
        <ListItemText
          primary={service.displayName}
          classes={{ primary: sideMenuClasses.listItemSpan }}
        />
        {is_user_maf_admin(userInfo) && (
          <button
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              handleDeleteService(event, service.id, service.displayName);
            }}
            aria-label={`Delete ${service.displayName}`}
            className={sideMenuClasses.deleteServiceTypeButtonStyle}
          >
            <DeleteIcon className={sideMenuClasses.deleteServiceTypeStyle} />
          </button>
        )}
      </ListItem>
    );
  };

  return (
    <>
      <Drawer
        className={sideMenuClasses.drawer}
        variant="permanent"
        anchor="left"
        classes={{
          paper: sideMenuClasses.drawerPaper,
          paperAnchorLeft: sideMenuClasses.paperAnchorLeft,
        }}
      >
        <div className={sideMenuClasses.drawerContainer}>
          <List className={sideMenuClasses.listStyleMain}>
            <ListItem
              button
              key={"BASICDETAILS"}
              component={NavLink}
              to={`${APP_ROUTES.CREATE_POS}${APP_ROUTES.BASIC_DETAILS}`}
              className={sideMenuClasses.listStyle}
              selected={pathname.includes(APP_ROUTES.BASIC_DETAILS)}
              disabled={is_side_menu_basic_details_disabled(userInfo)}
            >
              <ListItemIcon>
                {pathname.includes(APP_ROUTES.BASIC_DETAILS) ? (
                  <img
                    src={DashboardActive}
                    className={sideMenuClasses.listIconSVG}
                    alt="dashboardActive"
                  />
                ) : (
                  <img
                    src={DashboardMenu}
                    className={sideMenuClasses.listIconSVG}
                    alt="dashboard"
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary={STORE_SERVICE_LABELS.POS_DETAILS.toUpperCase()}
                classes={{ primary: sideMenuClasses.listItemSpan }}
              />
              {isCreated || isUpdated ? (
                <img src={CheckedIcon} alt="checked" />
              ) : null}
            </ListItem>

            {isArrayValid(enableServiceType) && (
              <ListItem
                button
                key={"SERVICESLOTS"}
                className={sideMenuClasses.listStyle}
                onClick={handleServiceClick}
                selected={pathname.includes(APP_ROUTES.SERVICE_AND_SLOTS)}
              >
                <ListItemIcon>
                  {pathname.includes(APP_ROUTES.SERVICE_AND_SLOTS) ? (
                    <img
                      src={ServiceActive}
                      className={sideMenuClasses.listIconSVG}
                      alt="servicesActive"
                    />
                  ) : (
                    <img
                      src={ProductsMenu}
                      className={sideMenuClasses.listIconSVG}
                      alt="services"
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={"SERVICE & SLOTS"}
                  classes={{ primary: sideMenuClasses.listItemSpan }}
                />
                <img
                  src={IconArrowClose}
                  alt="listClose"
                  className={`${sideMenuClasses.listIconArrow} ${sideBarState.openServices ? sideMenuClasses.rotateBy270 : ""
                    }`}
                />
              </ListItem>
            )}

            <Collapse
              in={sideBarState.openServices}
              timeout="auto"
              unmountOnExit
            >
              <List className={sideMenuClasses.nestedUl}>
                {isArrayValid(enableServiceType)
                  ? enableServiceType.map(renderServiceListItem)
                  : null}
              </List>
            </Collapse>
          </List>
        </div>
      </Drawer>
      {sideBarState.openDeleteServiceTypeDialog && (
        <DeleteServiceTypeDialog
          open={sideBarState.openDeleteServiceTypeDialog}
          onClose={() =>
            setSideBarState({
              ...sideBarState,
              openDeleteServiceTypeDialog: false,
              serviceId: null,
              serviceDisplayName: "",
            })
          }
          onDelete={handleDeleteServiceType}
          sideMenuClasses={sideMenuClasses}
          serviceId={sideBarState.serviceId}
          serviceDisplayName={sideBarState.serviceDisplayName}
        />
      )}
    </>
  );
}

SideBar.propTypes = {
  userInfo: PropTypes.object.isRequired,
};

export default React.memo(SideBar);
